import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssetCategoriesService } from '../assets/services/asset-categories.service';
import { AssetCategory } from '../models/asset-category';
import { HomesickCategories, Organization } from '../models/organization';
import { EntityFormService, Form } from '../util/entity-form.service';
import { Place, countryMap } from '../util/geography';
import {
  HomesickCategory,
  HomesickCategoryFormGroup,
} from './components/homesick-categories/homesick-categories.component';

export interface OrganizationForm extends Form<any> {
  name: FormControl<string>;
  street_address: FormControl<string>;
  street_address2: FormControl<string>;
  city: FormControl<string>;
  country: FormControl<string>;
  region: FormControl<string>;
  postal_code: FormControl<string>;
  discover_locations: FormControl<boolean>;
  any_fixed_is_home: FormControl<boolean>;
  default_homesick_days: FormControl<number>;
  homesick_categories: FormArray<FormGroup<HomesickCategoryFormGroup>>;
}

export type AdditionalFormData = {
  countries: Place[];
  categories: AssetCategory[];
};

@Injectable()
export class OrganizationFormService extends EntityFormService<
  Organization,
  OrganizationForm,
  AdditionalFormData
> {
  private orgReplay = new ReplaySubject<HomesickCategory[]>(1);
  orgCategories$: Observable<HomesickCategory[]> =
    this.orgReplay.asObservable();

  constructor(
    protected fb: FormBuilder,
    private categoriesService: AssetCategoriesService
  ) {
    super();
    super.initialize();
  }

  protected initializeForm() {
    this.form = this.fb.group({
      name: ['', []],
      street_address: ['', []],
      street_address2: ['', []],
      city: ['', []],
      country: ['', []],
      region: ['', []],
      postal_code: ['', []],
      discover_locations: [false, []],
      any_fixed_is_home: [false, []],
      default_homesick_days: [0, []],
      homesick_categories: this.fb.array<FormGroup<HomesickCategoryFormGroup>>(
        []
      ),
    });

    // Makes selectd region null when country is switched
    this.subscriptions.add(
      this.form.controls.country.valueChanges.subscribe(() => {
        this.form.controls.region.patchValue(null);
      })
    );
    this.subscriptions.add(
      this.form.controls.homesick_categories.valueChanges.subscribe(
        (categories) => {
          this.orgReplay.next(
            categories.map((c) => ({
              id: c.id,
              length: c.length,
              name: c.name,
            }))
          );
        }
      )
    );
  }

  // Builds the entity using the given form values
  public buildEntity(entity: Organization): Organization {
    const formValue = this.form.value;
    const homesick_categories =
      formValue.homesick_categories.reduce<HomesickCategories>((prev, curr) => {
        return { ...prev, [curr.id]: curr.length };
      }, {});

    return {
      ...entity,
      name: formValue.name,
      street_address: formValue.street_address,
      street_address2: formValue.street_address2,
      city: formValue.city,
      country: formValue.country,
      region: formValue.region,
      postal_code: formValue.postal_code,
      discover_locations: formValue.discover_locations,
      default_homesick_days: formValue.default_homesick_days,
      any_fixed_is_home: formValue.any_fixed_is_home,
      homesick_categories,
    };
  }

  loadAdditionalFormData(): Observable<AdditionalFormData> {
    // Loads the available countries
    return combineLatest([
      of(
        Object.keys(countryMap).map((code) => ({
          code: code,
          name: countryMap[code],
        }))
      ),
      this.categoriesService
        .getEntityList({}, { page: 0, perPage: 100 })
        .pipe(map((list) => list.entities)),
    ]).pipe(map(([countries, categories]) => ({ countries, categories })));
  }
}
